import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { concat, forkJoin, of } from 'rxjs';

import { DeviceConfigurationService } from './services/device-configuration.service';
import {
  DeviceConfigurationDtoIncoming,
  DeviceConfigurationDtoOutbound,
  UpdateDeviceConfigurationEvent,
} from './models/device-configuration.model';
import { DeviceConfigurationDialogComponent, DialogMode } from './add-device-configuration/device-configuration-view.component';
import { ToastService } from '../../shared/services/toast-service';
import { LanguageService } from '../../core/i18n/language.service';
import { PageableApiResponse } from '../api-response';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridDetailDirective } from '../../shared/components/grid/directives/grid-detail.directive';
import { GridColumn, GridSort, gridColumns } from '../../shared/components/grid/models/grid.model';

@Component({
  selector: 'app-device-configuration',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, DatePipe, Grid, GridPage, GridDetailDirective, DeviceConfigurationDialogComponent],
  templateUrl: './device-configuration.html',
  styleUrl: './device-configuration.css',
})
export class DeviceConfiguration implements OnInit {
  private readonly deviceConfigurationService = inject(DeviceConfigurationService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly configurations = signal<DeviceConfigurationDtoIncoming[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);
  readonly sort = signal<GridSort>({ sortBy: 'created', sortDirection: 'DESC' });
  readonly isLoading = signal(false);

  readonly isDialogOpen = signal(false);
  readonly dialogMode = signal<DialogMode>('add');
  readonly currentConfiguration = signal<DeviceConfigurationDtoIncoming | null>(null);

  readonly configId = (c: DeviceConfigurationDtoIncoming) => String(c.id);
  readonly asConfig = (row: unknown): DeviceConfigurationDtoIncoming => row as DeviceConfigurationDtoIncoming;

  readonly resolution = (c: DeviceConfigurationDtoIncoming): string =>
    c.displayWidth && c.displayHeight ? `${c.displayWidth}×${c.displayHeight}` : this.transloco.translate('deviceConfig.na');

  /** Columns as a computed so labels re-translate on a language switch. */
  readonly columns = computed<GridColumn<DeviceConfigurationDtoIncoming>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`deviceConfig.columns.${k}`);
    return gridColumns<DeviceConfigurationDtoIncoming>([
      { key: 'name', label: t('name'), type: 'text', textVariant: 'strong', sortable: false, alwaysVisible: true, width: 240 },
      { key: 'timezone', label: t('timezone'), type: 'text', sortable: false },
      { key: 'resolution', label: t('resolution'), type: 'text', sortable: false, width: 160, value: (c) => this.resolution(c) },
      { key: 'created', label: t('created'), type: 'date', sortable: true, width: 180 },
    ]);
  });

  ngOnInit(): void {
    this.loadConfigurations();
  }

  private loadConfigurations(): void {
    const { sortBy, sortDirection } = this.sort();
    this.isLoading.set(true);
    this.deviceConfigurationService
      .getDeviceConfigurationsPaginated(this.page(), this.size(), sortBy, sortDirection)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: PageableApiResponse<DeviceConfigurationDtoIncoming>) => {
          this.configurations.set(data.data);
          this.page.set(data.page);
          this.size.set(data.size);
          this.totalElements.set(data.totalElements);
          this.totalPages.set(data.totalPages);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show('Failed to load device configurations', 'error');
          this.isLoading.set(false);
        },
      });
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
    this.loadConfigurations();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.loadConfigurations();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.loadConfigurations();
  }

  onRowClick(config: DeviceConfigurationDtoIncoming): void {
    this.currentConfiguration.set(config);
  }

  /** Double-click (or the detail-panel Edit button) opens the edit dialog. */
  editConfiguration(config: DeviceConfigurationDtoIncoming): void {
    this.currentConfiguration.set(config);
    this.dialogMode.set('edit');
    this.isDialogOpen.set(true);
  }

  displayPortCount(config: DeviceConfigurationDtoIncoming): number {
    return config.displayPorts?.length ?? 0;
  }

  // ── Add / edit dialog ─────────────────────────────────────────────────

  openAddDeviceConfigurationDialog(): void {
    this.dialogMode.set('add');
    this.currentConfiguration.set(null);
    this.isDialogOpen.set(true);
  }

  closeAddDeviceConfigurationDialog(): void {
    this.isDialogOpen.set(false);
  }

  handleConfigurationSaved(deviceConfiguration: DeviceConfigurationDtoOutbound): void {
    this.deviceConfigurationService
      .postDeviceConfiguration(deviceConfiguration)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('Device configuration saved successfully', 'success');
          this.loadConfigurations();
        },
        error: () => this.toastService.show('Failed to save device configuration', 'error'),
      });
  }

  handleConfigurationUpdated(event: UpdateDeviceConfigurationEvent): void {
    const { configId, parentFields, newPorts, updatedPorts, deletedPortIds } = event;

    const putParent$ = this.deviceConfigurationService.putDeviceConfiguration(configId, {
      ...parentFields,
      displayPorts: [],
    });

    const portCalls = [
      ...deletedPortIds.map((id) => this.deviceConfigurationService.deleteDisplayPort(id)),
      ...newPorts.map((port) => this.deviceConfigurationService.addDisplayPort(configId, port)),
      ...updatedPorts.map(({ id, data }) => this.deviceConfigurationService.updateDisplayPort(id, data)),
    ];
    const portOps$ = portCalls.length > 0 ? forkJoin(portCalls) : of(null);

    concat(putParent$, portOps$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => this.toastService.show('Failed to update device configuration', 'error'),
        complete: () => {
          this.toastService.show('Device configuration updated successfully', 'success');
          this.loadConfigurations();
        },
      });
  }
}
